/**
 * Platform detection utilities.
 * P-11 (2026-04-18): Previously also hosted performPlatformUpdate() for
 * PWA update branching. That was removed with the autoUpdate migration —
 * Workbox + navigation reload handles all platforms uniformly now.
 * Remaining exports are used by PWA install flow and in-app-browser detection.
 */

/**
 * Detects if the current device is Android
 */
export function isAndroidDevice(): boolean {
  const userAgent = navigator.userAgent || '';
  return /Android/i.test(userAgent);
}

/**
 * Detects if the current browser is an in-app browser (Instagram, Facebook, TikTok, etc.)
 * These browsers have limited PWA support and often can't install apps
 */
export function isInAppBrowser(): boolean {
  const ua = navigator.userAgent || '';
  // Detect social media in-app browsers/WebViews
  return /FBAN|FBAV|FB_IAB|Instagram|Line|Twitter|TikTok|Snapchat|Pinterest|LinkedInApp|KAKAOTALK|Telegram/i.test(ua);
}

/**
 * Gets the name of the in-app browser if detected
 */
export function getInAppBrowserName(): string | null {
  const ua = navigator.userAgent || '';
  if (/Instagram/i.test(ua)) return 'Instagram';
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return 'Facebook';
  if (/TikTok/i.test(ua)) return 'TikTok';
  if (/Twitter/i.test(ua)) return 'Twitter';
  if (/Snapchat/i.test(ua)) return 'Snapchat';
  if (/Pinterest/i.test(ua)) return 'Pinterest';
  if (/LinkedInApp/i.test(ua)) return 'LinkedIn';
  if (/KAKAOTALK/i.test(ua)) return 'KakaoTalk';
  if (/Telegram/i.test(ua)) return 'Telegram';
  if (/Line/i.test(ua)) return 'LINE';
  return null;
}

export interface PlatformInfo {
  isIOS: boolean;
  isSafari: boolean;
  isStandalone: boolean; // PWA mode
  requiresHardReload: boolean;
  browserName: string;
}

/**
 * Detects if the current device is iOS (iPhone/iPad)
 */
export function isIOSDevice(): boolean {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // Check for iOS devices
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;

  // iOS 13+ on iPad may identify as Mac, so check for touch support
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

  return isIOS || isIPadOS;
}

/**
 * Detects if the current browser is Safari
 */
export function isSafariBrowser(): boolean {
  const userAgent = navigator.userAgent;
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
  return isSafari;
}

/**
 * Detects Chrome on iOS (CriOS). Since iOS 16.4 (2023), Chrome-iOS installs
 * PWAs direto pelo Share da barra de endereço — instrução própria, não mais
 * "abra no Safari". (Audit 21/07: o CriOS era detectado e ignorado.)
 */
export function isIOSChromeBrowser(): boolean {
  return isIOSDevice() && /CriOS/i.test(navigator.userAgent || '');
}

/**
 * Detects if the app is running in standalone mode (installed PWA)
 */
export function isStandaloneMode(): boolean {
  // Check if running in standalone mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  // iOS specific check
  const isIOSStandalone = (window.navigator as any).standalone === true;

  return isStandalone || isIOSStandalone;
}

/**
 * Gets comprehensive platform information
 */
export function getPlatformInfo(): PlatformInfo {
  const isIOS = isIOSDevice();
  const isSafari = isSafariBrowser();
  const isStandalone = isStandaloneMode();

  // iOS PWAs require hard reload because Service Worker support is limited
  const requiresHardReload = isIOS || (isSafari && isStandalone);

  // Detect browser name
  let browserName = 'Unknown';
  const userAgent = navigator.userAgent;

  if (isIOS) {
    browserName = isStandalone ? 'iOS PWA' : 'iOS Safari';
  } else if (/chrome|chromium|crios/i.test(userAgent)) {
    browserName = 'Chrome';
  } else if (/firefox|fxios/i.test(userAgent)) {
    browserName = 'Firefox';
  } else if (/safari/i.test(userAgent)) {
    browserName = 'Safari';
  } else if (/edg/i.test(userAgent)) {
    browserName = 'Edge';
  }

  return {
    isIOS,
    isSafari,
    isStandalone,
    requiresHardReload,
    browserName,
  };
}

// P-11 (2026-04-18): performPlatformUpdate / performIOSUpdate /
// performServiceWorkerUpdate / logPlatformInfo removed. With
// registerType: "autoUpdate" the Service Worker activates silently on every
// deploy and usePWAUpdate hook handles the reload via window.location.reload()
// on next navigation. No platform-specific update branching needed — Workbox
// + navigation-triggered reload works identically on iOS Safari, Android
// Chrome, and desktop. Platform DETECTION (getPlatformInfo, isIOSDevice,
// isSafariBrowser, isStandaloneMode, etc.) is kept because it's still used
// by PWA install flow and in-app-browser detection.
